/**
 * Gestion d'un "cache-buster" PERSISTANT pour les photos produit.
 *
 * Problème résolu :
 * Les photos produit ont un nom de fichier fixe par position
 * ({id}-c.jpg, {id}-z-1.jpg, ...). Quand on change la photo de couverture,
 * le CONTENU de {id}-c.jpg change mais l'URL reste identique. Le navigateur
 * ET le CDN (storage.efashion-paris.com / MinIO) servent alors l'ancienne
 * image en cache après un simple rafraîchissement.
 *
 * Un buster en mémoire (ref) est perdu au rafraîchissement. On le persiste
 * donc dans localStorage : à chaque opération photo (set cover, rotate,
 * delete, upload) on incrémente la version du produit. La version est ajoutée
 * en query string (?v=) ce qui change la clé de cache du navigateur et du CDN
 * et force le rechargement du contenu réel.
 */

const STORAGE_KEY = "productPhotoVersions";

type VersionMap = Record<number, number>;

function readMap(): VersionMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as VersionMap) : {};
  } catch {
    return {};
  }
}

function writeMap(map: VersionMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // localStorage indisponible (mode privé, quota) : on ignore silencieusement
  }
}

/**
 * Retourne la version actuelle d'un produit (0 si aucune opération photo
 * n'a encore eu lieu sur ce navigateur).
 */
export function getPhotoVersion(productId: number | null | undefined): number {
  if (productId == null) return 0;
  return readMap()[productId] ?? 0;
}

/**
 * Incrémente et persiste la version d'un produit. À appeler après toute
 * opération qui modifie le contenu des photos (set cover, rotate, delete,
 * upload). Retourne la nouvelle version.
 */
export function bumpPhotoVersion(productId: number | null | undefined): number {
  if (productId == null) return 0;
  const map = readMap();
  const next = Math.max(map[productId] ?? 0, Date.now());
  map[productId] = next;
  writeMap(map);
  return next;
}

/**
 * Ajoute la version persistante (?v=) à une URL d'image produit.
 * Si aucune version n'est connue, l'URL est renvoyée telle quelle.
 */
export function withPhotoVersion(
  url: string,
  productId: number | null | undefined,
): string {
  if (!url) return "";
  const version = getPhotoVersion(productId);
  if (!version) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}v=${version}`;
}
